import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { quotesFeed, setProjectQuoteStatus, deleteQuote, quotePdfUrl, renumberQuote, openQuotePdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { FileDown, Pencil, Trash2, Mail, FileText, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import FactuurVanuitOfferteDialog from "../components/facturen/FactuurVanuitOfferteDialog";
import OverviewControls from "../components/overview/OverviewControls";
import { useOverviewPreferences } from "../hooks/useOverviewPreferences";
import { applySort, naturalCompare, statusOrderComparator } from "../lib/sortOverview";

const STATUS_LABEL = {
  draft: "Concept",
  sent: "Verzonden",
  approved: "Goedgekeurd",
  rejected: "Geweigerd",
  // v14al — user-facing label "Gefactureerd" → "Afgerond".
  // Backendwaarde "invoiced" blijft ongewijzigd voor backward compatibility.
  invoiced: "Afgerond",
};

// v6 F15.2 §4.2 — Neutrale statustonen (rustig off-white met subtiele rand).
const STATUS_TONE = {
  draft:    "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  sent:     "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  approved: "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  rejected: "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  invoiced: "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
};

const STATUS_OPTIONS = ["draft", "sent", "approved", "rejected", "invoiced"];
// Business workflow-order (voor sortering): concept → verstuurd → goedgekeurd → afgewezen → gefactureerd
const QUOTE_STATUS_ORDER = ["draft", "sent", "approved", "invoiced", "rejected"];

// v14aj — Sorteeropties Offertes. Geen totaalbedrag (quotes hebben geen
// persistent total; berekening op de rug van de feed geeft O(n) extra werk
// per page-load — bewust uitgesteld naar backlog).
const QUOTE_SORT_OPTIONS = [
  { value: "number_desc", label: "Offertenr aflopend" },
  { value: "number_asc", label: "Offertenr oplopend" },
  { value: "client_asc", label: "Klant A → Z" },
  { value: "client_desc", label: "Klant Z → A" },
  { value: "project_asc", label: "Project A → Z" },
  { value: "project_desc", label: "Project Z → A" },
  { value: "date_desc", label: "Datum nieuw → oud" },
  { value: "date_asc", label: "Datum oud → nieuw" },
  { value: "status_asc", label: "Status (workflow)" },
];

const numeric = (s) => {
  const m = String(s || "").match(/(\d+)/g);
  return m ? m.map(Number).reduce((a, b) => a * 10000 + b, 0) : 0;
};

const QUOTE_COMPARATORS = {
  number_desc: (a, b) => numeric(b.quote_number) - numeric(a.quote_number),
  number_asc: (a, b) => numeric(a.quote_number) - numeric(b.quote_number),
  client_asc: (a, b) => naturalCompare(a.client_name, b.client_name),
  client_desc: (a, b) => naturalCompare(b.client_name, a.client_name),
  project_asc: (a, b) => naturalCompare(a.project_name, b.project_name),
  project_desc: (a, b) => naturalCompare(b.project_name, a.project_name),
  date_desc: (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")),
  date_asc: (a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")),
  status_asc: statusOrderComparator((r) => r.quote_status || "draft", QUOTE_STATUS_ORDER, "asc"),
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("nl-BE"); } catch { return "—"; }
};

export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [invoiceWizardQuoteId, setInvoiceWizardQuoteId] = useState(null);
  const { sort, setSort } = useOverviewPreferences("quotes", "list", "date_desc");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [filtered, all] = await Promise.all([
        quotesFeed(params),
        (q || status) ? quotesFeed({}) : Promise.resolve(null),
      ]);
      setRows(filtered);
      if (all) setAllRows(all); else setAllRows(filtered);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status]);

  // v14aj — Auto-refresh op focus + visibility (zelfde patroon als Klanten/Projecten).
  useEffect(() => {
    const onFocus = () => { load(); };
    const onVis = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [q, status]);

  const totals = useMemo(() => {
    const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
    allRows.forEach((r) => { counts[r.quote_status] = (counts[r.quote_status] || 0) + 1; });
    return counts;
  }, [allRows]);

  const sorted = useMemo(() => applySort(rows, sort, QUOTE_COMPARATORS), [rows, sort]);

  const onChangeStatus = async (row, newStatus) => {
    try {
      await setProjectQuoteStatus(row.project_id, newStatus);
      toast.success(`Status: ${STATUS_LABEL[newStatus]}`);
      const upd = (prev) => prev.map((r) => r.project_id === row.project_id ? { ...r, quote_status: newStatus } : r);
      setRows(upd); setAllRows(upd);
    } catch { toast.error("Status updaten mislukt"); }
  };

  const onDelete = async (id) => {
    try {
      await deleteQuote(id);
      toast.success("Offerte verwijderd");
      setRows((prev) => prev.filter((r) => r.id !== id));
      setAllRows((prev) => prev.filter((r) => r.id !== id));
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const onRenumber = async (row) => {
    try {
      const updated = await renumberQuote(row.id);
      toast.success(`Hernummerd: ${row.quote_number || "—"} → ${updated.quote_number}`);
      const apply = (prev) => prev.map((r) => r.id === row.id ? { ...r, quote_number: updated.quote_number } : r);
      setRows(apply); setAllRows(apply);
    } catch { toast.error("Hernummeren mislukt"); }
  };

  const emailTo = (row) => {
    const url = quotePdfUrl(row.id);
    const subject = encodeURIComponent(`Offerte ${row.quote_number || ""} — ${row.project_name || ""}`);
    const body = encodeURIComponent(`Beste,\n\nIn bijlage de offerte ${row.quote_number || ""}.\n\n${url}\n\nMet vriendelijke groet.`);
    window.location.href = `mailto:${row.client_email || ""}?subject=${subject}&body=${body}`;
  };

  const openPdf = async (id) => {
    try {
      await openQuotePdf(id);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401) toast.error("Sessie verlopen. Log opnieuw in.");
      else if (s === 404) toast.error("Offerte niet gevonden.");
      else toast.error("PDF kon niet worden geopend.");
    }
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold">EmmaMeasure</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight">Offertes</h1>
          <p className="text-sm text-[#4F4F4D] mt-1">Alle offertes uit alle projecten — zoek, filter, beheer status, verstuur.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs" data-testid="status-chips">
          {STATUS_OPTIONS.map((s) => {
            const active = status === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(active ? "" : s)}
                aria-pressed={active}
                data-testid={`status-chip-${s}`}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition ${
                  active
                    ? "bg-[#2C2C2A] text-white border-[#2C2C2A]"
                    : `${STATUS_TONE[s]} hover:bg-[#F0EFEA]`
                }`}
              >
                <span>{STATUS_LABEL[s]}</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                    active ? "bg-white/25 text-white" : "bg-white text-[#4F4F4D] border border-[#E5E2DC]"
                  }`}
                  data-testid={`status-count-${s}`}
                >
                  {totals[s] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* v14aj — Uniforme control-row (zoek + status + sort + view). */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-3 sm:p-4">
        <OverviewControls
          moduleKey="quotes"
          search={{ value: q, onChange: setQ, placeholder: "Zoek op offertenr, klant, project…", testId: "quotes-search" }}
          filters={[{
            id: "status",
            placeholder: "Alle statussen",
            value: status || "all",
            onChange: (v) => setStatus(v === "all" ? "" : v),
            options: [{ value: "all", label: "Alle statussen" }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABEL[s] }))],
            testId: "quotes-status-filter",
          }]}
          sort={{ value: sort, onChange: setSort, options: QUOTE_SORT_OPTIONS }}
        />
      </div>

      {/* Desktop lijst */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden" data-testid="quotes-list-view">
        <div
          className="hidden md:grid gap-3 px-4 py-2 bg-[#FAF9F6] border-b border-[#E5E2DC] text-[11px] font-semibold uppercase tracking-wide text-[#6E6E6C]"
          style={{ gridTemplateColumns: "130px minmax(0,1.2fr) minmax(0,1.4fr) 100px 140px 240px" }}
          data-testid="quotes-list-header"
        >
          <div>Offertenr</div>
          <div>Klant</div>
          <div>Project</div>
          <div>Datum</div>
          <div>Status</div>
          <div className="text-right">Acties</div>
        </div>

        {loading && <div className="text-center text-[#5D7259] py-6">Laden…</div>}
        {!loading && sorted.length === 0 && (
          <div className="py-10 text-center space-y-1.5">
            <p className="text-sm text-[#4F4F4D]">Nog geen offertes.</p>
            <p className="text-xs text-[#A8A7A0]">Maak een offerte aan vanuit een <Link to="/projecten" className="text-[#D96C4A] underline">project</Link>.</p>
          </div>
        )}

        <ul className="divide-y divide-[#E5E2DC]">
          {sorted.map((r) => (
            <li key={r.id} className="hover:bg-[#FAF9F6]" data-testid={`quote-row-${r.quote_number || r.id}`}>
              {/* Desktop grid */}
              <div
                className="hidden md:grid gap-3 px-4 py-3 items-center text-sm"
                style={{ gridTemplateColumns: "130px minmax(0,1.2fr) minmax(0,1.4fr) 100px 140px 240px" }}
              >
                <div className="min-w-0 truncate font-mono text-xs text-[#4F4F4D]" title={r.quote_number || ""} data-testid={`row-number-${r.id}`}>
                  {r.quote_number || "—"}
                </div>
                <div className="min-w-0 truncate text-[#D96C4A] font-medium" title={r.client_name || ""} data-testid={`row-client-${r.id}`}>
                  {r.client_name || "—"}
                </div>
                <div className="min-w-0 truncate text-[#2C2C2A]" title={r.project_name || ""} data-testid={`row-project-${r.id}`}>
                  {r.project_name || "—"}
                </div>
                <div className="text-[#4F4F4D] tabular-nums text-xs" data-testid={`row-date-${r.id}`}>
                  {formatDate(r.created_at)}
                </div>
                <div data-testid={`row-status-${r.id}`}>
                  <Select value={r.quote_status} onValueChange={(v) => onChangeStatus(r, v)}>
                    <SelectTrigger className={`h-9 rounded-lg text-xs ${STATUS_TONE[r.quote_status] || ""}`} data-testid={`status-select-${r.quote_number || r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <QuoteActions r={r} openPdf={openPdf} emailTo={emailTo} onRenumber={onRenumber} onDelete={onDelete} onFactureer={setInvoiceWizardQuoteId} />
              </div>

              {/* Mobile card */}
              <div className="md:hidden px-4 py-3 space-y-2" data-testid={`quote-card-${r.quote_number || r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-[#4F4F4D]">{r.quote_number || "—"}</div>
                    <div className="font-medium text-[#2C2C2A] truncate">{r.project_name || "—"}</div>
                    <div className="text-xs text-[#D96C4A] truncate">{r.client_name || "—"}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_TONE[r.quote_status]}>{STATUS_LABEL[r.quote_status]}</Badge>
                </div>
                <div className="text-xs text-[#4F4F4D] tabular-nums">{formatDate(r.created_at)}</div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" className="h-9 rounded-lg gap-1 text-xs" onClick={() => openPdf(r.id)} data-testid={`pdf-mobile-${r.quote_number || r.id}`}>
                    <FileDown className="w-3.5 h-3.5" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="h-9 rounded-lg gap-1 text-xs" onClick={() => emailTo(r)}>
                    <Mail className="w-3.5 h-3.5" /> E-mail
                  </Button>
                  <Button asChild size="sm" variant="outline" className="h-9 rounded-lg gap-1 text-xs">
                    <Link to={`/projecten/${r.project_id}`}><Pencil className="w-3.5 h-3.5" /> Bewerk</Link>
                  </Button>
                  {r.quote_status === "approved" && (
                    <Button
                      size="sm" variant="outline"
                      className="h-9 rounded-lg gap-1 text-xs text-[#5D7259]"
                      onClick={() => setInvoiceWizardQuoteId(r.id)}
                      data-testid={`factureer-mobile-${r.quote_number || r.id}`}
                    >
                      <Receipt className="w-3.5 h-3.5" /> Factureer
                    </Button>
                  )}
                  <Select value={r.quote_status} onValueChange={(v) => onChangeStatus(r, v)}>
                    <SelectTrigger className="h-9 rounded-lg text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <FactuurVanuitOfferteDialog
        open={!!invoiceWizardQuoteId}
        onOpenChange={(v) => { if (!v) setInvoiceWizardQuoteId(null); }}
        presetQuoteId={invoiceWizardQuoteId}
      />
    </div>
  );
}

// v14aj — Rechts uitgelijnde actie-groep voor de desktop-lijst.
function QuoteActions({ r, openPdf, emailTo, onRenumber, onDelete, onFactureer }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => openPdf(r.id)} data-testid={`pdf-${r.quote_number || r.id}`} title="PDF openen">
        <FileDown className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => emailTo(r)} data-testid={`email-${r.quote_number || r.id}`} title="E-mail">
        <Mail className="w-4 h-4" />
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#2F557E]" data-testid={`renumber-${r.quote_number || r.id}`} title="Hernummer">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Offerte hernummeren?</AlertDialogTitle>
            <AlertDialogDescription>
              {r.quote_number || "—"} wordt vervangen door het volgende beschikbare nummer volgens Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRenumber(r)} className="bg-[#2F557E] hover:bg-[#234262]">Hernummer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Button asChild size="sm" variant="ghost" className="h-9 w-9 p-0" data-testid={`edit-${r.quote_number || r.id}`} title="Bewerk">
        <Link to={`/projecten/${r.project_id}`}><Pencil className="w-4 h-4" /></Link>
      </Button>
      {r.quote_status === "approved" && (
        <Button
          size="sm" variant="ghost"
          className="h-9 w-9 p-0 text-[#5D7259]"
          onClick={() => onFactureer(r.id)}
          title="Factureer"
          data-testid={`factureer-${r.quote_number || r.id}`}
        >
          <Receipt className="w-4 h-4" />
        </Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-[#9C2A1A]" data-testid={`delete-${r.quote_number || r.id}`} title="Verwijder">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Offerte verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              {r.quote_number || ""} — {r.project_name}. Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(r.id)} className="bg-[#9C2A1A] hover:bg-[#7e2114]">Verwijder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
